import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Upload, BarChart3, Sparkles, CheckCircle2, ArrowRight, FileSpreadsheet } from "lucide-react";

/**
 * OnboardingModal — shown automatically once when a user with 0 products logs in.
 * 3-step guided tour: 1) download template, 2) import, 3) see results.
 * Stores a flag in localStorage so it only shows once per user.
 */
const STORAGE_KEY = "smartstockai_onboarding_seen";

export default function OnboardingModal() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    // Only show if user actually has no data (fresh account)
    api.get("/dashboard/summary").then((r) => {
      if ((r.data?.products_count || 0) === 0) {
        setOpen(true);
      }
    }).catch(() => {});
  }, []);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const downloadTemplate = (kind) => {
    const a = document.createElement("a");
    a.href = `${backendUrl}/api/templates/${kind}.csv`;
    a.download = `smartstockai-${kind === "products" ? "produtos" : "vendas"}-modelo.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const goToImport = () => {
    close();
    navigate("/app/integrations");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-2xl bg-card border-primary/30 glow-coral" data-testid="onboarding-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="font-display text-xl">Bem-vindo ao SmartStockAI</DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Em 3 passos rápidos você terá previsões prontas.</p>
            </div>
          </div>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center justify-between gap-2 my-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex-1 flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${
                  step >= n
                    ? "bg-primary border-primary text-white"
                    : "bg-secondary/40 border-border text-muted-foreground"
                }`}
              >
                {step > n ? <CheckCircle2 className="w-4 h-4" /> : n}
              </div>
              {n < 3 && (
                <div className={`flex-1 h-0.5 ${step > n ? "bg-primary" : "bg-border"}`} />
              )}
            </div>
          ))}
        </div>

        {step === 1 && (
          <Step
            icon={Download}
            title="Baixe os modelos de planilha"
            description="2 modelos prontos: catálogo de produtos e histórico de vendas. Edite no Excel, Google Sheets ou qualquer planilha."
            tipTitle="Dica"
            tipText="O modelo de vendas vem com 90 dias de exemplos — basta substituir pelos seus dados reais para máxima precisão da IA."
            actions={
              <>
                <Button
                  data-testid="ob-download-products"
                  variant="outline"
                  onClick={() => downloadTemplate("products")}
                  className="flex-1"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Modelo de produtos
                </Button>
                <Button
                  data-testid="ob-download-sales"
                  variant="outline"
                  onClick={() => downloadTemplate("sales")}
                  className="flex-1"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Modelo de vendas
                </Button>
              </>
            }
          />
        )}

        {step === 2 && (
          <Step
            icon={Upload}
            title="Preencha e importe os dois arquivos"
            description="Cole seus produtos e suas vendas históricas nos modelos. Depois faça upload — leva segundos."
            tipTitle="Importante"
            tipText="Importe primeiro o catálogo, depois o histórico de vendas. Os SKUs precisam ser os mesmos nos dois arquivos."
            actions={
              <Button
                data-testid="ob-go-import"
                onClick={goToImport}
                className="w-full bg-primary hover:bg-primary/90"
              >
                <Upload className="w-4 h-4 mr-2" /> Ir para a página de importação
              </Button>
            }
          />
        )}

        {step === 3 && (
          <Step
            icon={BarChart3}
            title="Veja suas previsões na hora"
            description="Assim que importar, o Dashboard já mostra: produtos em risco de ruptura, estoque parado, recomendações de compra e receita projetada para 30 dias."
            tipTitle="O que a IA vai calcular"
            tipText="Previsão por SKU, score de saúde do estoque (A–F), alertas priorizados, e sugestão de quanto comprar de cada produto."
            actions={
              <Button
                data-testid="ob-finish"
                onClick={close}
                className="w-full bg-primary hover:bg-primary/90"
              >
                Começar agora <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            }
          />
        )}

        <div className="flex items-center justify-between mt-2 pt-3 border-t border-border">
          <button
            data-testid="ob-skip"
            onClick={close}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Pular tutorial
          </button>
          <div className="flex gap-2">
            {step > 1 && (
              <Button
                data-testid="ob-back"
                variant="ghost"
                size="sm"
                onClick={() => setStep(step - 1)}
              >
                Voltar
              </Button>
            )}
            {step < 3 && (
              <Button
                data-testid="ob-next"
                size="sm"
                onClick={() => setStep(step + 1)}
                className="bg-primary hover:bg-primary/90"
              >
                Próximo <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Step = ({ icon: Icon, title, description, tipTitle, tipText, actions }) => (
  <div className="space-y-4">
    <div className="flex items-start gap-3">
      <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      <div>
        <div className="font-display font-semibold text-lg">{title}</div>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </div>
    </div>
    <div className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 text-xs">
      <span className="font-medium text-amber-300">{tipTitle}: </span>
      <span className="text-foreground/80">{tipText}</span>
    </div>
    <div className="flex flex-col sm:flex-row gap-2">{actions}</div>
  </div>
);
